import { Injectable, computed, effect, signal, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { AppNotification, NotificationCategory, OrganizationInvite, OrgUserRoles, ProjectInvite, ProjectUserRoles, ProjectUsers, OrganizationUsers } from '@models';
import { AppNotificationService } from './app-notification.service';
import { MachineApiService } from './machine-api.service';
import { OrganizationInviteService } from './organization-invite.service';
import { OrganizationProjectsService } from './organization.service';
import { OrganizationService } from './organization.service';
import { OrganizationUsersService } from './organization.service';
import { ProjectInviteService } from './project-invite.service';
import { ProjectService } from './project.service';
import { ProjectUsersService } from './project.service';
import { ToastService } from './toast.service';
import { UserFacade } from '../state/user/user.facade';
import { WorkspaceFacade } from '../state/workspace/workspace.facade';
import { lookupUserEmails } from '../utils/user-email.util';
import { Operator, appstraxUsers } from '@appstrax/services/auth';

type WsMessage =
  | { type: 'hello'; userId: string; connectedAt: number }
  | { type: 'notification'; toUserId: string; notificationId?: string | null; payload?: any; sentAt: number };

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private userStore = inject(UserFacade);
  private projectUsersService = inject(ProjectUsersService);
  private orgUsersService = inject(OrganizationUsersService);
  private orgProjectsService = inject(OrganizationProjectsService);
  private projectService = inject(ProjectService);
  private organizationService = inject(OrganizationService);
  private notificationsSig = signal<AppNotification[]>([]);
  private invitesSig = signal<Array<{
    id: string;
    type: 'project' | 'organization';
    name: string;
    inviter: string;
    timestamp: string;
    role: string;
    raw: ProjectInvite | OrganizationInvite;
  }>>([]);
  private loadingSig = signal<boolean>(false);
  private ws: WebSocket | null = null;

  // Cache for mapping userId -> email (for display)
  private userEmailById: Record<string, string> = {};

  readonly notifications = computed(() => this.notificationsSig());
  readonly invites = computed(() => this.invitesSig());
  readonly unreadCount = computed(() => this.notificationsSig().filter((n) => !n.read && !n.archived).length);
  readonly loading = computed(() => this.loadingSig());

  constructor(
    private api: AppNotificationService,
    private projInvites: ProjectInviteService,
    private orgInvites: OrganizationInviteService,
    private store: WorkspaceFacade,
    private toast: ToastService,
    private machineApi: MachineApiService,
  ) {
    // Auto-connect when we have a user.
    effect(() => {
      const userId = this.userStore.current()?.id ?? '';
      if (!userId) return;
      void this.loadForUser(userId);
      void this.loadInvitesForUser(userId);
      this.ensureWs(userId);
    });
  }

  private wsUrl(userId: string): string {
    const base = environment.machineApiUrl;
    const wsBase = base.startsWith('https://')
      ? base.replace('https://', 'wss://')
      : base.replace('http://', 'ws://');
    return `${wsBase}/ws/notifications?userId=${encodeURIComponent(userId)}`;
  }

  private ensureWs(userId: string) {
    // Avoid reconnect storms if effect runs multiple times.
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    try {
      this.ws = new WebSocket(this.wsUrl(userId));
      this.ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(String(evt.data ?? '{}')) as WsMessage;
          if (msg.type === 'notification' && msg.toUserId === userId) {
            void this.loadForUser(userId);
            void this.loadInvitesForUser(userId);
          }
        } catch {}
      };
      this.ws.onclose = () => {
        this.ws = null;
      };
      this.ws.onerror = () => {
        // Keep silent; page still works via DB.
      };
    } catch {
      this.ws = null;
    }
  }

  async loadForUser(userId: string): Promise<void> {
    if (!userId) return;
    this.loadingSig.set(true);
    try {
      const res = await this.api.find({
        where: { toUserId: userId } as any,
      });
      const items = (res.data ?? []) as AppNotification[];
      // Newest first.
      items.sort((a: any, b: any) => ((b.timestamp ?? 0) - (a.timestamp ?? 0)));
      this.notificationsSig.set(items);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to load notifications', 'Notifications');
      this.notificationsSig.set([]);
    } finally {
      this.loadingSig.set(false);
    }
  }

  private async hydrateUserEmails(userIds: string[]): Promise<void> {
    this.userEmailById = await lookupUserEmails(userIds, this.userEmailById);
  }

  private userEmail(userId: string | undefined | null): string {
    if (!userId) return '';
    return this.userEmailById[userId] ?? '';
  }

  async loadInvitesForUser(userId: string): Promise<void> {
    if (!userId) return;
    try {
      const [projRes, orgRes] = await Promise.all([
        this.projInvites.find({ where: { toUserId: userId, status: 'pending' } as any }),
        this.orgInvites.find({ where: { toUserId: userId, status: 'pending' } as any }),
      ]);

      const projInvites = (projRes.data ?? []) as ProjectInvite[];
      const orgInvites = (orgRes.data ?? []) as OrganizationInvite[];

      // Best-effort name lookups for projects/orgs referenced by pending invites.
      // Kept out of workspace state: the user may not be a member of them (yet).
      const projectNameById = new Map((this.store.projects() ?? []).map((x) => [x.id, x.name]));
      const orgNameById = new Map((this.store.organizations() ?? []).map((x) => [x.id, x.name]));
      const missingProjectIds = Array.from(new Set(projInvites.map((p) => p.projectId).filter(Boolean))).filter(
        (id) => !projectNameById.has(id)
      );
      const missingOrgIds = Array.from(new Set(orgInvites.map((o) => o.organizationId).filter(Boolean))).filter(
        (id) => !orgNameById.has(id)
      );
      await Promise.all([
        (async () => {
          if (!missingProjectIds.length) return;
          try {
            const res = await this.projectService.find({ where: { id: { [Operator.IN]: missingProjectIds } } });
            for (const x of res.data ?? []) projectNameById.set(x.id, x.name);
          } catch {
            // Permission/visibility may prevent pre-accept reads; fall back to IDs.
          }
        })(),
        (async () => {
          if (!missingOrgIds.length) return;
          try {
            const res = await this.organizationService.find({ where: { id: { [Operator.IN]: missingOrgIds } } });
            for (const x of res.data ?? []) orgNameById.set(x.id, x.name);
          } catch {
            // ignore; fall back to IDs.
          }
        })(),
      ]);

      // Resolve inviter userIds -> emails for display.
      const inviterIds = Array.from(
        new Set(
          [...projInvites.map((p) => p.fromUserId), ...orgInvites.map((o) => o.fromUserId)]
            .map((x) => String(x ?? '').trim())
            .filter(Boolean)
        )
      );
      await this.hydrateUserEmails(inviterIds);

      const rows: Array<{
        id: string;
        type: 'project' | 'organization';
        name: string;
        inviter: string;
        timestamp: string;
        role: string;
        raw: any;
      }> = [];

      for (const p of projInvites) {
        const projName = projectNameById.get(p.projectId) ?? p.projectId;
        const inviterEmail = this.userEmail(p.fromUserId);
        rows.push({
          id: p.id,
          type: 'project',
          name: projName,
          inviter: inviterEmail || p.fromUserId,
          timestamp: new Date(p.timestamp ?? Date.now()).toLocaleString(),
          role: String((p as any).role ?? ''),
          raw: p,
        });
      }
      for (const o of orgInvites) {
        const orgName = orgNameById.get(o.organizationId) ?? o.organizationId;
        const inviterEmail = this.userEmail(o.fromUserId);
        rows.push({
          id: o.id,
          type: 'organization',
          name: orgName,
          inviter: inviterEmail || o.fromUserId,
          timestamp: new Date(o.timestamp ?? Date.now()).toLocaleString(),
          role: String((o as any).role ?? ''),
          raw: o,
        });
      }
      rows.sort((a, b) => (b.raw.timestamp ?? 0) - (a.raw.timestamp ?? 0));
      this.invitesSig.set(rows);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to load invites', 'Notifications');
      this.invitesSig.set([]);
    }
  }

  private async patchNotification(
    id: string,
    patch: Partial<AppNotification>,
    errorMessage: string,
    skip?: (current: AppNotification) => boolean
  ): Promise<void> {
    const current = this.notificationsSig().find((n) => n.id === id) ?? null;
    if (!current || (skip && skip(current))) return;
    try {
      const updated = { ...(current as any), ...patch } as AppNotification;
      await this.api.save(updated);
      this.notificationsSig.update((list) => list.map((n) => (n.id === id ? updated : n)));
    } catch (e: any) {
      this.toast.error(e?.message ?? errorMessage, 'Notifications');
    }
  }

  async markAsRead(id: string) {
    await this.patchNotification(id, { read: true }, 'Failed to update notification', (current) => !!current.read);
  }

  async markAllAsRead() {
    const unread = this.notificationsSig().filter((n) => !n.read && !n.archived);
    for (const n of unread) {
      await this.markAsRead(n.id);
    }
  }

  async archive(id: string) {
    await this.patchNotification(id, { archived: true }, 'Failed to archive notification', (current) => !!current.archived);
  }

  async restore(id: string) {
    await this.patchNotification(id, { archived: false }, 'Failed to restore notification', (current) => !current.archived);
  }

  private async findProjectUser(projectId: string, userId: string): Promise<ProjectUsers | null> {
    try {
      const res = await this.projectUsersService.find({ where: { projectId, userId } as any });
      return res.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  private async findOrgUser(organizationId: string, userId: string): Promise<OrganizationUsers | null> {
    try {
      const res = await this.orgUsersService.find({ where: { organizationId, userId } as any });
      return res.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  private async ensureProjectMembership(projectId: string, userId: string, role: any): Promise<void> {
    if (await this.findProjectUser(projectId, userId)) return;
    const link = new ProjectUsers();
    link.projectId = projectId;
    link.userId = userId;
    (link as any).role = role;
    await this.projectUsersService.save(link as any);
  }

  private async ensureOrgMembership(organizationId: string, userId: string, role: any): Promise<void> {
    if (await this.findOrgUser(organizationId, userId)) return;
    const link = new OrganizationUsers();
    link.organizationId = organizationId;
    link.userId = userId;
    (link as any).role = role;
    await this.orgUsersService.save(link as any);
  }

  private async ensureOrgMembershipsForProject(projectId: string, userId: string): Promise<void> {
    let mappings: any[] = [];
    try {
      const res = await this.orgProjectsService.find({ where: { projectId } as any });
      mappings = res.data ?? [];
    } catch {}
    const orgIds = Array.from(new Set(mappings.map((m: any) => m.organizationId).filter(Boolean)));
    for (const orgId of orgIds) {
      const cached = this.store.orgUsers().find((ou) => ou.organizationId === orgId && ou.userId === userId) ?? null;
      if (cached) continue;
      await this.ensureOrgMembership(orgId, userId, OrgUserRoles.USER);
    }
  }

  private async grantProjectMemberships(userId: string, projectIds: string[], projRole: any): Promise<void> {
    for (const pid of projectIds) {
      const projectId = String(pid ?? '').trim();
      if (!projectId) continue;
      await this.ensureProjectMembership(projectId, userId, projRole);
    }
  }

  private async acceptProjectInviteFlow(
    projectId: string,
    userId: string,
    role: any,
    inviteId: string,
  ): Promise<void> {
    await this.ensureProjectMembership(projectId, userId, role);
    await this.ensureOrgMembershipsForProject(projectId, userId);
    try {
      await this.projInvites.delete(inviteId);
    } catch {}
  }

  private async acceptOrganizationInviteFlow(
    organizationId: string,
    userId: string,
    orgRole: any,
    projectIds: string[],
    projRole: any,
    inviteId: string,
  ): Promise<void> {
    await this.ensureOrgMembership(organizationId, userId, orgRole);
    await this.grantProjectMemberships(userId, projectIds, projRole);
    try {
      await this.orgInvites.delete(inviteId);
    } catch {}
  }

  private newOrganizationInvite(
    toUserId: string,
    fromUserId: string,
    organizationId: string,
    role: any,
    note?: string,
  ): OrganizationInvite {
    const inv = new OrganizationInvite();
    inv.toUserId = toUserId;
    inv.fromUserId = fromUserId;
    inv.organizationId = organizationId;
    (inv as any).role = role;
    inv.status = 'pending';
    inv.timestamp = Date.now();
    inv.note = note;
    return inv;
  }

  private async publishOrganizationInviteNotification(
    toUserId: string,
    organizationName: string,
    inviterEmail: string,
    fromUserId: string,
    savedInvite: OrganizationInvite,
    data: Record<string, unknown>,
  ): Promise<void> {
    await this.sendToUser(
      toUserId,
      `Organization invite: ${organizationName}`,
      `You were invited to organization "${organizationName}" by ${inviterEmail}`,
      'invite',
      {
        inviteType: 'organization',
        inviteId: savedInvite.id,
        organizationId: savedInvite.organizationId,
        organizationName,
        inviterEmail,
        fromUserId,
        ...data,
      },
    );
  }

  private currentUserIdOrEmpty(): string {
    return this.userStore.current()?.id ?? '';
  }

  private findLoadedInviteRow(inviteId: string) {
    return this.invitesSig().find((x) => x.id === inviteId) ?? null;
  }

  private readInviteSenderContext(): { fromUserId: string; inviterEmail: string } {
    const fromUserId = this.currentUserIdOrEmpty();
    const inviterEmail = this.userStore.current()?.email ?? fromUserId;
    if (!fromUserId) throw new Error('Not authenticated');
    return { fromUserId, inviterEmail };
  }

  private organizationDisplayName(organizationId: string): string {
    return (this.store.organizations() ?? []).find((o: any) => o.id === organizationId)?.name ?? organizationId;
  }

  private readNotificationInviteData(n: AppNotification): {
    userId: string;
    inviteId: string;
    inviteType: string;
    data: any;
  } | null {
    const userId = this.currentUserIdOrEmpty();
    if (!userId) return null;
    const data = ((n as any)?.data ?? {}) as any;
    return {
      userId,
      inviteId: data?.inviteId ? String(data.inviteId) : '',
      inviteType: data?.inviteType ? String(data.inviteType) : '',
      data,
    };
  }

  private async deleteInviteRecord(type: 'project' | 'organization', inviteId: string): Promise<void> {
    if (type === 'project') {
      await this.projInvites.delete(inviteId);
      return;
    }
    await this.orgInvites.delete(inviteId);
  }

  private async finalizeInviteAcceptance(userId: string): Promise<void> {
    await this.store.refreshWorkspaceData();
    await this.loadInvitesForUser(userId);
    this.toast.success('Invite accepted', 'Notifications');
  }

  async acceptInvite(inviteId: string): Promise<void> {
    const row = this.findLoadedInviteRow(inviteId);
    const userId = this.currentUserIdOrEmpty();
    if (!row || !userId) return;
    try {
      if (row.type === 'project') {
        const inv = row.raw as ProjectInvite;
        await this.acceptProjectInviteFlow(inv.projectId, inv.toUserId, (inv as any).role, inv.id);
      } else {
        const inv = row.raw as OrganizationInvite;
        const projectIds = Array.isArray((inv as any).projectIds) ? (inv as any).projectIds : [];
        await this.acceptOrganizationInviteFlow(
          inv.organizationId,
          inv.toUserId,
          ((inv as any).role ?? OrgUserRoles.USER) as any,
          projectIds,
          ((inv as any).projectRole ?? ProjectUserRoles.MEMBER) as any,
          inv.id,
        );
      }
      await this.finalizeInviteAcceptance(userId);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to accept invite', 'Notifications');
    }
  }

  /**
   * Accept an invite directly from an AppNotification payload.
   * This avoids relying on the invites list being hydrated and ensures we route
   * to the correct invite type (project vs organization).
   */
  async acceptInviteFromNotification(n: AppNotification): Promise<void> {
    const parsed = this.readNotificationInviteData(n);
    if (!parsed) return;
    const { userId, inviteId, inviteType, data } = parsed;

    // Fallback to legacy behavior (invites tab / already loaded).
    if (!inviteType || !inviteId) {
      if (inviteId) await this.acceptInvite(inviteId);
      return;
    }

    try {
      if (inviteType === 'organization') {
        const organizationId = String(data.organizationId ?? '');
        if (!organizationId) throw new Error('Missing organizationId on invite notification');
        const projectIds = Array.isArray(data.projectIds) ? data.projectIds : [];
        await this.acceptOrganizationInviteFlow(
          organizationId,
          userId,
          (data.orgRole ?? data.role ?? OrgUserRoles.USER) as any,
          projectIds,
          (data.projectRole ?? ProjectUserRoles.MEMBER) as any,
          inviteId,
        );
      } else if (inviteType === 'project') {
        const projectId = String(data.projectId ?? '').trim();
        if (!projectId) throw new Error('Missing projectId on invite notification');
        await this.acceptProjectInviteFlow(
          projectId,
          userId,
          (data.role ?? ProjectUserRoles.MEMBER) as any,
          inviteId,
        );
      } else {
        await this.acceptInvite(inviteId);
      }

      await this.finalizeInviteAcceptance(userId);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to accept invite', 'Notifications');
    }
  }

  async declineInvite(inviteId: string): Promise<void> {
    const row = this.findLoadedInviteRow(inviteId);
    const userId = this.currentUserIdOrEmpty();
    if (!row || !userId) return;
    try {
      await this.deleteInviteRecord(row.type, (row.raw as ProjectInvite | OrganizationInvite).id);
      await this.loadInvitesForUser(userId);
      this.toast.info('Invite declined', 'Notifications');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to decline invite', 'Notifications');
    }
  }

  async declineInviteFromNotification(n: AppNotification): Promise<void> {
    const parsed = this.readNotificationInviteData(n);
    if (!parsed) return;
    const { userId, inviteId, inviteType } = parsed;
    if (!inviteId) return;

    try {
      if (inviteType === 'organization') {
        try {
          await this.orgInvites.delete(inviteId);
        } catch {}
      } else if (inviteType === 'project') {
        try {
          await this.projInvites.delete(inviteId);
        } catch {}
      } else {
        await this.declineInvite(inviteId);
      }

      await this.loadInvitesForUser(userId);
      this.toast.info('Invite declined', 'Notifications');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to decline invite', 'Notifications');
    }
  }

  /**
   * Create a notification in the DB and push realtime signal via machine-api.
   */
  async sendToUser(toUserId: string, title: string, message: string, category: NotificationCategory = 'general', data?: any) {
    const userId = this.userStore.current()?.id ?? '';
    if (!userId) throw new Error('Not authenticated');
    const n = new AppNotification();
    n.toUserId = toUserId;
    n.title = title;
    n.message = message;
    n.category = category;
    n.read = false;
    n.archived = false;
    n.timestamp = Date.now();
    n.data = data ?? undefined;
    const saved = await this.api.save(n);

    try {
      await this.machineApi.postJson('/api/notifications/publish', {
        toUserId,
        notificationId: saved.id,
      });
    } catch {
      // If WS publish fails, DB still has it; recipient will see it on refresh.
    }

    return saved;
  }

  async sendProjectInvite(toUserId: string, projectId: string, role: any, note?: string) {
    const fromUserId = this.userStore.current()?.id ?? '';
    const inviterEmail = this.userStore.current()?.email ?? fromUserId;
    if (!fromUserId) throw new Error('Not authenticated');
    const projectName =
      (this.store.projects() ?? []).find((p: any) => p.id === projectId)?.name ?? projectId;
    const inv = new ProjectInvite();
    inv.toUserId = toUserId;
    inv.fromUserId = fromUserId;
    inv.projectId = projectId;
    (inv as any).role = role;
    inv.status = 'pending';
    inv.timestamp = Date.now();
    inv.note = note;
    const savedInvite = await this.projInvites.save(inv);
    await this.sendToUser(
      toUserId,
      `Project invite: ${projectName}`,
      `You were invited to project "${projectName}" by ${inviterEmail}`,
      'invite',
      { inviteType: 'project', inviteId: savedInvite.id, projectId, role, projectName, inviterEmail, fromUserId }
    );
    return savedInvite;
  }

  async sendOrganizationInvite(toUserId: string, organizationId: string, role: any, note?: string) {
    const { fromUserId, inviterEmail } = this.readInviteSenderContext();
    const organizationName = this.organizationDisplayName(organizationId);
    const inv = this.newOrganizationInvite(toUserId, fromUserId, organizationId, role, note);
    const savedInvite = await this.orgInvites.save(inv);
    await this.publishOrganizationInviteNotification(
      toUserId,
      organizationName,
      inviterEmail,
      fromUserId,
      savedInvite,
      { role },
    );
    return savedInvite;
  }

  async sendOrganizationBulkInvite(
    toUserId: string,
    organizationId: string,
    orgRole: any,
    projectIds: string[],
    projectRole: any = ProjectUserRoles.MEMBER,
    note?: string,
  ) {
    const { fromUserId, inviterEmail } = this.readInviteSenderContext();
    const organizationName = this.organizationDisplayName(organizationId);
    const inv = this.newOrganizationInvite(toUserId, fromUserId, organizationId, orgRole, note);
    (inv as any).projectIds = Array.from(new Set((projectIds ?? []).filter(Boolean)));
    (inv as any).projectRole = projectRole;
    const savedInvite = await this.orgInvites.save(inv);
    await this.publishOrganizationInviteNotification(
      toUserId,
      organizationName,
      inviterEmail,
      fromUserId,
      savedInvite,
      {
        orgRole,
        projectIds: (inv as any).projectIds,
        projectRole,
      },
    );
    return savedInvite;
  }
}


